/**
 * Auto-Review Configuration Component
 *
 * Allows Developer Master to configure HOD auto-review settings
 * for automated maintenance request approval
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";

const AutoReviewConfig = () => {
  const { currentUser } = useAuth();
  const [config, setConfig] = useState({
    enabled: true,
    autoApprovePriorities: ["low", "medium"],
    autoApproveRoles: ["faculty", "admin"],
    autoApproveCategories: ["general", "cleaning"],
    requireManualReview: ["urgent"],
    maxAutoApprovalPerDay: 50,
    categorySpecificSettings: {},
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Available options
  const priorityOptions = ["low", "medium", "high", "urgent"];
  const roleOptions = ["student", "scholar", "faculty", "admin"];
  const categoryOptions = [
    "electrical",
    "plumbing",
    "hvac",
    "general",
    "cleaning",
    "security",
  ];

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = await currentUser.getIdToken();
      const response = await fetch("/api/v1/system/auto-review-config", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to load auto-review configuration");
      }

      const data = await response.json();
      setConfig(data);
    } catch (err) {
      setError(err.message);
      console.error("Error loading auto-review config:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = await currentUser.getIdToken();
      const response = await fetch("/api/v1/system/auto-review-config", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error("Failed to save auto-review configuration");
      }

      const data = await response.json();
      setSuccess(
        data.message || "Auto-review configuration saved successfully"
      );
    } catch (err) {
      setError(err.message);
      console.error("Error saving auto-review config:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleArrayItem = (field, item) => {
    setConfig((prev) => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter((i) => i !== item)
        : [...prev[field], item],
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <Typography>Loading auto-review configuration...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight={700}>
          <SettingsIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Auto-Review Configuration
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Configure automated HOD review settings for maintenance requests
        </Typography>
      </Box>

      {/* Error and Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Settings */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Global Auto-Review Settings"
              action={
                <Tooltip title="Refresh configuration">
                  <IconButton onClick={loadConfig} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              {/* Master Enable/Disable */}
              <FormControlLabel
                control={
                  <Switch
                    checked={config.enabled}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        enabled: e.target.checked,
                      }))
                    }
                  />
                }
                label="Enable Auto-Review System"
                sx={{ mb: 3 }}
              />

              <Divider sx={{ my: 2 }} />

              {/* Max Approvals Per Day */}
              <TextField
                fullWidth
                label="Max Auto-Approvals Per Day"
                type="number"
                value={config.maxAutoApprovalPerDay}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    maxAutoApprovalPerDay: parseInt(e.target.value),
                  }))
                }
                helperText="Safety limit to prevent excessive auto-approvals"
                sx={{ mb: 3 }}
              />

              {/* Auto-Approve Priorities */}
              <Typography variant="h6" gutterBottom>
                Auto-Approve Priorities
              </Typography>
              <Box sx={{ mb: 3 }}>
                {priorityOptions.map((priority) => (
                  <FormControlLabel
                    key={priority}
                    control={
                      <Switch
                        checked={config.autoApprovePriorities.includes(
                          priority
                        )}
                        onChange={() =>
                          handleToggleArrayItem(
                            "autoApprovePriorities",
                            priority
                          )
                        }
                      />
                    }
                    label={priority.charAt(0).toUpperCase() + priority.slice(1)}
                  />
                ))}
              </Box>

              {/* Auto-Approve Roles */}
              <Typography variant="h6" gutterBottom>
                Auto-Approve User Roles
              </Typography>
              <Box sx={{ mb: 3 }}>
                {roleOptions.map((role) => (
                  <FormControlLabel
                    key={role}
                    control={
                      <Switch
                        checked={config.autoApproveRoles.includes(role)}
                        onChange={() =>
                          handleToggleArrayItem("autoApproveRoles", role)
                        }
                      />
                    }
                    label={role.charAt(0).toUpperCase() + role.slice(1)}
                  />
                ))}
              </Box>

              {/* Auto-Approve Categories */}
              <Typography variant="h6" gutterBottom>
                Auto-Approve Categories
              </Typography>
              <Box sx={{ mb: 3 }}>
                {categoryOptions.map((category) => (
                  <FormControlLabel
                    key={category}
                    control={
                      <Switch
                        checked={config.autoApproveCategories.includes(
                          category
                        )}
                        onChange={() =>
                          handleToggleArrayItem(
                            "autoApproveCategories",
                            category
                          )
                        }
                      />
                    }
                    label={category.charAt(0).toUpperCase() + category.slice(1)}
                  />
                ))}
              </Box>

              {/* Require Manual Review */}
              <Typography variant="h6" gutterBottom>
                Always Require Manual Review
              </Typography>
              <Box sx={{ mb: 3 }}>
                {priorityOptions.map((priority) => (
                  <FormControlLabel
                    key={priority}
                    control={
                      <Switch
                        checked={config.requireManualReview.includes(priority)}
                        onChange={() =>
                          handleToggleArrayItem("requireManualReview", priority)
                        }
                      />
                    }
                    label={priority.charAt(0).toUpperCase() + priority.slice(1)}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Information Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title="Auto-Review Rules"
              avatar={<InfoIcon color="primary" />}
            />
            <CardContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                The auto-review system automatically approves maintenance
                requests based on the configured rules:
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                Current Active Rules:
              </Typography>

              {config.enabled ? (
                <Box>
                  <Typography variant="body2" paragraph>
                    • <strong>Priority-based:</strong> Auto-approve{" "}
                    {config.autoApprovePriorities.map((p) => (
                      <Chip key={p} label={p} size="small" sx={{ mx: 0.5 }} />
                    ))}{" "}
                    priority requests
                  </Typography>

                  <Typography variant="body2" paragraph>
                    • <strong>Role-based:</strong> Auto-approve requests from{" "}
                    {config.autoApproveRoles.map((r) => (
                      <Chip key={r} label={r} size="small" sx={{ mx: 0.5 }} />
                    ))}{" "}
                    users
                  </Typography>

                  <Typography variant="body2" paragraph>
                    • <strong>Category-based:</strong> Auto-approve{" "}
                    {config.autoApproveCategories.map((c) => (
                      <Chip key={c} label={c} size="small" sx={{ mx: 0.5 }} />
                    ))}{" "}
                    category requests
                  </Typography>

                  <Typography variant="body2" paragraph>
                    • <strong>Manual review required:</strong>{" "}
                    {config.requireManualReview.map((p) => (
                      <Chip
                        key={p}
                        label={p}
                        size="small"
                        color="warning"
                        sx={{ mx: 0.5 }}
                      />
                    ))}{" "}
                    priority requests
                  </Typography>
                </Box>
              ) : (
                <Alert severity="warning" size="small">
                  Auto-review is currently disabled
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 3, textAlign: "right" }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={saveConfig}
          disabled={saving}
          sx={{ minWidth: 150 }}
        >
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </Box>
    </Box>
  );
};

export default AutoReviewConfig;
